import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { AgentsAutoSearchResult, AutoCompleteResult, AutoSearchApiService } from '@rma/generic/api/auto-search';
import { AgencyProfile, AgentProfile, PagedList, ProfilePageTypes, ProfileTheme, isAgentProfile } from '@rma/generic/domain/types';
import { LoadingIndicatorDirective } from '@rma/generic/ui/loading-indicator';
import { SplitMarketingLayoutComponent } from '@rma/generic/ui/split-marketing-layout';
import { Environment, StaticImageUrlPipe } from '@rma/generic/util/environment';
import { applyWithDialogDefaults } from '@rma/generic/util/material';
import { rmaShareReplay } from '@rma/generic/util/operators/combined';
import { IntercomService } from '@rma/generic/util/tracking/intercom';
import { AgencyClaimModal, ClaimAgencyModalComponent } from '@rma/profiles/claim-office';
import { ProfileService } from '@rma/profiles/public/api-legacy-profile';
import { AutoCompleteControlComponent } from '@rma/search/public/ui-autocomplete';
import { RmaFocusedHeaderComponent } from '@rma/site/public/ui-focused-header';
import { Observable, Subject, filter, map, merge, of, scan, startWith, switchMap, withLatestFrom } from 'rxjs';

interface SelectedProfile {
  isAgent: boolean;
  theme: ProfileTheme;
  name: string;
  website?: string;
  isClaimed: boolean;
  agencyName?: string;
  summary?: string;
  agencyProfileUrl?: string;
  agencyProfileId?: number;
  code: string;
  stub: string;
}

@Component({
  selector: 'rma-search-profile',
  imports: [
    AsyncPipe,
    AutoCompleteControlComponent,
    LoadingIndicatorDirective,
    MatDialogModule,
    RmaFocusedHeaderComponent,
    RouterModule,
    SplitMarketingLayoutComponent,
    StaticImageUrlPipe,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('searchprofile'), TranslocoPipe],
  templateUrl: './search-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchProfileComponent {
  private readonly autoSearchApiService = inject(AutoSearchApiService);
  private readonly dialogService = inject(MatDialog);
  private readonly environment = inject(Environment);
  private readonly profileService = inject(ProfileService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly intercomService = inject(IntercomService);

  private readonly take = 10;
  private readonly searchTermChanges = new Subject<string>();
  private readonly skipincrementer = new Subject<number>();

  protected readonly searchResults$: Observable<PagedList<AgentsAutoSearchResult>>;
  protected readonly alreadyClaimed$ = new Subject<boolean>();
  protected readonly loading$ = new Subject<boolean>();
  protected readonly initialValue: string | null = null;

  public constructor() {
    this.intercomService.initaliseWithoutCurrentUser();
    const searchTerm$ = this.searchTermChanges.asObservable();
    const skip$ = merge(searchTerm$.pipe(map(() => 0)), this.skipincrementer).pipe(scan((accum, a) => (a === 0 ? 0 : accum + a), 0));

    this.searchResults$ = this.initSearchResults(skip$, searchTerm$);

    if (this.activatedRoute.snapshot.queryParamMap.has('email')) {
      this.initialValue = this.activatedRoute.snapshot.queryParamMap.get('email');
    }
  }

  public onSelectedResult(event: MatAutocompleteSelectedEvent) {
    this.alreadyClaimed$.next(false);
    this.loading$.next(true);
    const value: AutoCompleteResult = event.option.value;
    // we may be able to agnosticify these, but I'll draw the line here for now.
    // some witchcraft happens here and we lose the enum type. TODO MC. string comparison for now.
    // ideally we do not care what they are!
    const profilePageType = value.autoSearchType === 'Agents' ? ProfilePageTypes.RealEstateAgent : ProfilePageTypes.RealEstateAgency;

    this.profileService
      .getProfileByCode(profilePageType, value.id, ' ', true)
      .pipe(map((profile) => this.profileToSelectedProfile(profile)))
      .subscribe((p) => this.claimProfile(p));
  }

  public updateSearchTerm(value: string): void {
    this.searchTermChanges.next(value);
  }

  public onViewedAll() {
    this.skipincrementer.next(this.take);
  }

  private initSearchResults(skip$: Observable<number>, searchTerm$: Observable<string>) {
    const initialResults: PagedList<AgentsAutoSearchResult> = { total: 0, skip: 0, take: 0, results: [] };

    return skip$.pipe(
      withLatestFrom(searchTerm$),
      switchMap(([skip, searchTerm]) => {
        return searchTerm === ''
          ? of(initialResults)
          : this.autoSearchApiService.getAgenciesAgents({
              includePdrLicenseSearch: true, // this should be the only place this is true.
              skip,
              take: this.take,
              searchTerm,
              isClaimedWeight: 0, // if we are looking for profiles to claim, de prioritise claimed profiles.
            });
      }),
      withLatestFrom(skip$),
      scan(
        (accum, [results, skip]) => (skip === 0 ? results : { ...accum, skip, results: [...accum.results, ...results.results] }),
        initialResults,
      ),
      startWith(initialResults),
      rmaShareReplay(),
    );
  }

  private claimProfile(selectedProfile: SelectedProfile): void {
    this.loading$.next(false);
    if (selectedProfile.isClaimed) {
      this.alreadyClaimed$.next(true);
      return;
    }

    if (selectedProfile.isAgent) {
      this.router.navigate([`/real-estate-agent/${selectedProfile.stub}/claim-profile/verification`], { queryParamsHandling: 'preserve' });
      return;
    }

    this.openAgencyClaimDialog({
      agencyProfileId: selectedProfile.agencyProfileId ?? 0,
      agencyName: selectedProfile.name,
      agencyProfileUrl: selectedProfile.agencyProfileUrl ?? '',
      domain: this.environment.domain,
    });
  }

  private openAgencyClaimDialog(claim: AgencyClaimModal) {
    this.dialogService
      .open(ClaimAgencyModalComponent, applyWithDialogDefaults({ data: { ...claim } }))
      .afterClosed()
      .pipe(filter((result) => Boolean(result)))
      // in absence of agencyURL route to home as opposed to /undefined
      .subscribe(() => this.router.navigate([`${claim.agencyProfileUrl || ''}`]));
  }

  private profileToSelectedProfile(profile: AgentProfile | AgencyProfile): SelectedProfile {
    const isAgent = isAgentProfile(profile);
    const baseProfile = {
      isAgent,
      theme: profile.theme,
      website: profile.about.website,
      isClaimed: profile.about.isClaimed,
    };

    const profileSpecific = isAgent
      ? {
          name: profile.agentName,
          agencyName: profile.agencyName,
          code: profile.agentCode,
          stub: profile.agentStub,
        }
      : {
          name: profile.agencyName,
          summary: profile.about.summary,
          agencyProfileUrl: profile.agencyProfileUrl,
          agencyProfileId: profile.agencyProfileId,
          code: profile.agencyCode,
          stub: profile.agencyStub,
        };

    return { ...baseProfile, ...profileSpecific };
  }
}
